/**
 * Pure derivation of PresenceRibbon's title/detail copy from the live-agent
 * handoff phase — factored out so the phase→copy mapping is unit-testable
 * without mounting the ribbon component (mirrors handoff-banner-text.ts's
 * pattern, which this module replaces as part of the v3 turn 5 visual
 * polish: a warm persistent presence ribbon + graceful-end recap, instead of
 * the blue banner treatment).
 *
 * Ribbon states map onto HandoffPhase (src/types/chat.ts) as follows:
 *
 *   - COLLECTING / WAITING → the same "looking for a guide" copy. Folded
 *     together because HandoffPhase has no dedicated sub-state for "form
 *     submitted, still queued" distinct from WAITING — see HandoffPhase's
 *     JSDoc. The ribbon is mounted for the whole CHAT/HANDOFF conversation
 *     column (see page.tsx), so it's visible during COLLECTING too (where
 *     ContactInfoCard is the separate inline form surface; the ribbon above
 *     it just keeps showing the ambient "we're finding you a guide" status).
 *   - CONNECTED → the agent's name + the "Art of Living support" tagline.
 *     There is no separate "agent accepted but hasn't sent a first message
 *     yet" sub-state in HandoffPhase — AGENT_JOINED transitions phase
 *     straight to CONNECTED (see useHandoff.ts's agentJoined) — so per the
 *     locked v3 design ("if not, treat as connected"), no distinct
 *     "{name} is connecting…" copy is implemented here.
 *   - ENDED → no ribbon (null). HandoffRecapCard (handoff-recap-card.tsx)
 *     takes over once a handoff ends — a one-time transcript entry, not a
 *     persistent ribbon.
 *   - TIMED_OUT → its own static (non-pulsing) ribbon variant: "no guide
 *     available right now" copy, no "End chat"/"Cancel request" action (the
 *     handoff is already over — there's nothing left to cancel). Distinct
 *     from ENDED precisely so a timeout gets a visible, ownable moment
 *     instead of silently vanishing like a resolved/user-ended handoff does.
 *     That moment isn't permanent, though: `recoveryWindowClosed` (see
 *     `hasResumedChatAfterTimeout` below, wired from page.tsx) makes this
 *     return null the same as ENDED once the window closes — TIMED_OUT is a
 *     terminal phase nothing ever transitions out of within a session, so
 *     without an explicit stopping rule this ribbon would otherwise pin
 *     itself above the transcript for the rest of the conversation.
 *
 * Deliberately does NOT surface a queue position or ETA for ANY phase — no
 * backend data source exists for either, and fabricating one would mislead
 * the user. This includes TIMED_OUT: the copy explains no guide is
 * available, never a retry countdown or position estimate.
 */
import { HandoffPhase, MessageRole, type Message } from '@/types/chat';

export interface PresenceRibbonText {
  title: string;
  detail: string;
}

/** Fallback when a rep has joined but the join event didn't carry a display
 *  name — mirrors handoff-banner-text.ts's DEFAULT_AGENT_LABEL so the ribbon
 *  never fabricates a name that wasn't actually reported. */
const DEFAULT_AGENT_LABEL = 'our support team';

/**
 * Returns the ribbon's title/detail copy for a given handoff phase, or
 * `null` when no ribbon should render (HandoffPhase.ENDED, or TIMED_OUT once
 * `recoveryWindowClosed`). Callers should guard on `handoff` being non-null
 * themselves (same precedent as handoffBannerText — see PresenceRibbon).
 *
 * @param recoveryWindowClosed Only consulted for TIMED_OUT (see
 *   `hasResumedChatAfterTimeout` below) — ignored for every other phase.
 *   Defaults false so every existing call site keeps showing the ribbon for
 *   the whole TIMED_OUT phase unless a caller opts in to the stopping rule.
 */
export function presenceRibbonText(
  phase: HandoffPhase,
  agentDisplayName?: string,
  recoveryWindowClosed = false
): PresenceRibbonText | null {
  if (phase === HandoffPhase.ENDED) return null;
  if (phase === HandoffPhase.TIMED_OUT && recoveryWindowClosed) return null;

  if (phase === HandoffPhase.CONNECTED) {
    return {
      title: `Connected to ${agentDisplayName || DEFAULT_AGENT_LABEL}`,
      detail: '· Art of Living support',
    };
  }

  if (phase === HandoffPhase.TIMED_OUT) {
    // No queue position/ETA here either — same rationale as the module
    // docstring: there's no backend signal for either, and the copy is
    // about explaining what happened, not promising a retry.
    return {
      title: 'No guide available right now',
      detail: '· Sara can still help',
    };
  }

  // COLLECTING and WAITING both read as "still looking for a guide" — see
  // module docstring.
  return {
    title: 'Waiting for a guide',
    detail: '· you can keep typing',
  };
}

/**
 * Whether the TIMED_OUT ribbon's "legitimate window" has closed — see
 * `presenceRibbonText`'s `recoveryWindowClosed` param. TIMED_OUT is a
 * terminal phase nothing ever transitions out of within a session (unlike
 * ENDED — see HandoffPhase's JSDoc in types/chat.ts), so without an
 * explicit stopping rule this ribbon would sit pinned above the transcript
 * — "No guide available right now" — for the rest of the conversation, long
 * after that stopped being the user's actual situation.
 *
 * The window is open through the "no rep available, here are your options"
 * moment (surfaced inline in the transcript by HandoffTimeoutPanel — see
 * page.tsx) and CLOSES the moment the user resumes ordinary chatting with
 * Sara, i.e. sends a new message — whether or not they ever engaged with the
 * recovery panel at all. That's deliberately keyed on a USER-authored row,
 * not "any new row": the personalized timeout apology (persisted by
 * HandoffRelay._deadline_watchdog) is itself a SYSTEM row that lands around
 * the same WS tick as the TIMED_OUT transition (see Defect 4's writeup), and
 * must not immediately close the window it exists to introduce.
 *
 * `sinceIndex` is the transcript length as of the last render BEFORE this
 * handoff was observed as TIMED_OUT (see page.tsx's baseline tracking) —
 * without it, "any USER row" would already be true from message #1 of any
 * real conversation, since the user obviously typed something to get here.
 */
export function hasResumedChatAfterTimeout(
  messages: readonly Pick<Message, 'role'>[],
  sinceIndex: number
): boolean {
  return messages.slice(sinceIndex).some((m) => m.role === MessageRole.USER);
}
